import React, { useState, useEffect, FocusEvent } from "react";
import { Link } from "react-router-dom";
import { ActionHandlerFunction, CurrentThread, PaginationItem } from "@preveil-api";
import { MailUIActionTypes, CheckboxStates, CheckboxStatesTypes, GlobalConstants, ClientDefaultMailboxesType, isDraftMailbox, isTrashMailbox, isSearchMailbox } from "src/common";
import { Checkbox, Icon, Pagination, PaginationDisplay, ToolbarButton, ToolbarDropdownItem, ToolbarWrapper } from "src/components";

type AllProps = {
  isLoading: boolean;
  thread?: CurrentThread;
  mailbox_name: string;
  pagination?: PaginationItem;
  selected: string[];
  handleAction: ActionHandlerFunction;
}

function ToolbarComponent(props: AllProps) {
  const { isLoading, thread, mailbox_name, pagination, selected, handleAction } = props;
  const [btn_disabled, setButtonDisabled] = useState<boolean>(selected.length <= 0);
  const [disabled, setDisabled] = useState<boolean>(false);

  useEffect(() => {
    !!pagination && setDisabled(pagination.totalRows <= 0 && !thread);
  }, [pagination, thread]);

  useEffect(() => {
    setButtonDisabled(selected.length <= 0 && !thread);
  }, [selected, thread]);

  // Description: Get the selected all checkbox state
  function getSelectedState(): CheckboxStatesTypes {
    let ck_state: CheckboxStatesTypes = CheckboxStates.empty;
    if (!!pagination) {
      ck_state = (!selected.length ? CheckboxStates.empty :
        (selected.length < GlobalConstants.LIMIT_NUMBER_THREAD_TO_FETCH && selected.length < pagination.totalRows) ?
          CheckboxStates.indeterminate : CheckboxStates.checked);
    }
    return ck_state;
  }

  // Description: Is current mailbox a client mailbox (starred, unread, outbox) OR Drafts (DA-2910)
  // NOTE: Move action is not allowed for those
  function isInvalidMoveMailbox(): boolean {
    return ClientDefaultMailboxesType.includes(mailbox_name) || isDraftMailbox(mailbox_name);
  }

  const left_content = (
    <>
      {!!thread ?
        <Link className="btn btn-icon btn-sm" to={`/mail/${encodeURIComponent(mailbox_name)}/${!!pagination ? pagination.pageIndex + 1 : 1}`}>
          <Icon className="pv-icon-arrow-left" />
        </Link> :
        <Checkbox className="sr-only"
          onChange={(e: FocusEvent<HTMLInputElement>) => handleAction({ actionType: MailUIActionTypes.Select, params: { selected: e.target.checked } })}
          value="all" label="Select All" selected={getSelectedState()} />
      }
      <span className="divider divider--mail-nudge" />
    </>
  );

  return <ToolbarWrapper
    leftContent={left_content}
    pagination={!thread && !!pagination ? <Pagination pagination={pagination} handleAction={handleAction} /> : <></>}
    paginationInfo={!thread && !!pagination ? <PaginationDisplay pagination={pagination} /> : <></>}
    refreshAction={() => handleAction({ actionType: MailUIActionTypes.Refresh })}
    refreshTooltipContent="Refresh Mailbox"
    refreshDisabled={isSearchMailbox(mailbox_name)}
    isLoading={isLoading}
    disabled={disabled}
  >
    {
      !isTrashMailbox(mailbox_name) ?
      <ToolbarButton
        onClick={() => handleAction({ actionType: MailUIActionTypes.Star, params: { messages: thread?.messages } })}
        ariaLabel="Star Selected"
        tooltipContent={`Star ${!thread ? "Selected" : ""} Messages`}
        disabled={btn_disabled}>
        <Icon className="pv-icon-unstarred" />
      </ToolbarButton> : <></>
    }
    <ToolbarButton
      onClick={() => handleAction({ actionType: MailUIActionTypes.UpdateSeenFlag, params: { messages: thread?.messages, remove: true } })}
      ariaLabel="Mark Selected Unread"
      tooltipContent={`Mark ${!thread ? "Selected" : ""} Messages Unread`}
      disabled={btn_disabled}>
      <Icon className="ficon-mail" />
    </ToolbarButton>
    {
      !isInvalidMoveMailbox() ?
      <ToolbarButton
        onClick={() => handleAction({ actionType: MailUIActionTypes.MoveModal, params: true })}
        ariaLabel="Move Selected"
        tooltipContent={`Move ${!thread ? "Selected" : ""} Messages`}
        disabled={btn_disabled}>
        <Icon className="pv-icon-move" />
      </ToolbarButton> : <></>
    }
    <ToolbarButton
      onClick={() => handleAction({ actionType: MailUIActionTypes.Delete, params: { messages: thread?.messages } })}
      ariaLabel="Delete Selected"
      tooltipContent={`Delete ${!thread ? "Selected" : ""} Messages`}
      disabled={btn_disabled}>
      <Icon className="ficon-trash-2" />
    </ToolbarButton>
    <ToolbarDropdownItem
      tooltipContent={`Mark ${!thread ? "selected" : ""} messages as read`}
      onClick={() => handleAction({ actionType: MailUIActionTypes.UpdateSeenFlag, params: { messages: thread?.messages, remove: false } })}
      disabled={btn_disabled}>
      {`Mark ${!thread ? "selected" : "messages"} as read`}
    </ToolbarDropdownItem>
    {
      !isTrashMailbox(mailbox_name) ?
      <ToolbarDropdownItem
        tooltipContent={`Remove Star from ${!thread ? "all selected" : ""} messages`}
        onClick={() => handleAction({ actionType: MailUIActionTypes.Star, params: { messages: thread?.messages, is_starred: true } })}
        disabled={btn_disabled}>
        {`Remove Star from ${!thread ? "selected" : "thread"}`}
      </ToolbarDropdownItem> : <></>
    }
  </ToolbarWrapper>;
}

export default React.memo(ToolbarComponent);
